import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import BulkEditBanner, { useBulkEdits, BulkEditToggle } from "../../components/BulkEditBanner";

const blank = { name: "", customer_type: "retailer", phone: "", email: "", gst_number: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "", default_discount_pct: 0, default_special_discount_pct: 0, default_cash_discount_pct: 0, default_gst_pct: 12, notes: "" };
const TYPES = ["doctor", "distributor", "retailer", "patient", "other"];

export default function AccountsCustomers() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [f, setF] = useState(blank);
    const [q, setQ] = useState("");
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);

    const { setCell, cellValue, dirtyIds, dirtyPatches, reset: resetBulk } = useBulkEdits(items);

    const load = async () => { const { data } = await api.get("/customers", { params: { q: q || undefined } }); setItems(data); };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const save = async () => {
        if (!f.name) return toast.error("Name required");
        try {
            if (editing === "new") await api.post("/customers", f);
            else await api.put(`/customers/${editing}`, f);
            toast.success("Saved"); setEditing(null); setF(blank); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/customers/${id}`); load(); };

    const saveBulk = async () => {
        const patches = dirtyPatches();
        if (patches.length === 0) return;
        setBulkBusy(true);
        let ok = 0, fail = 0;
        for (const { id, original, patch } of patches) {
            try {
                const merged = { ...blank, ...original, ...patch };
                // eslint-disable-next-line no-await-in-loop
                await api.put(`/customers/${id}`, merged);
                ok += 1;
            } catch (e) {
                fail += 1;
                toast.error(`${original?.name || id}: ${e.response?.data?.detail || "Save failed"}`);
            }
        }
        setBulkBusy(false);
        if (ok > 0) toast.success(`Saved · ${ok} customer${ok === 1 ? "" : "s"}${fail ? ` (${fail} failed)` : ""}`);
        resetBulk();
        await load();
    };

    return (
        <div data-testid="accounts-customers" className="space-y-4">
            <Toaster position="top-center" />
            <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                    <div className="label-eyebrow text-sky-700">Ledger</div>
                    <h1 className="font-serif text-4xl text-stone-900">Customer master</h1>
                </div>
                <div className="flex gap-2">
                    <BulkEditToggle
                        active={bulkMode}
                        onClick={() => { setBulkMode(!bulkMode); resetBulk(); }}
                        testId="toggle-bulk-mode"
                    />
                    <Button onClick={() => { setEditing("new"); setF(blank); }} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="add-customer"><Plus className="w-4 h-4 mr-2" />Add customer</Button>
                </div>
            </div>

            {bulkMode && (
                <BulkEditBanner
                    dirtyCount={dirtyIds.length}
                    busy={bulkBusy}
                    onReset={resetBulk}
                    onSave={saveBulk}
                    helperText="Edit Name, Phone, City, GSTIN, GST %, Discount %, State, Type inline. Save commits every row at once."
                    testIdPrefix="bulk"
                />
            )}

            <div className="bg-white border border-stone-200 p-3 flex gap-2">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name/phone/email…" className="rounded-none border-stone-300" />
                <Button onClick={load} className="rounded-none">Search</Button>
            </div>

            {editing && (
                <div className="bg-white border border-stone-200 p-5 space-y-3">
                    <div className="flex justify-between"><h2 className="font-serif text-2xl">{editing === "new" ? "New" : "Edit"} customer</h2><Button variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button></div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <CF label="Name *" v={f.name} on={(v) => setF({ ...f, name: v })} t="cust-name" />
                        <div><Label className="text-xs uppercase tracking-widest text-stone-500">Type</Label>
                            <Select value={f.customer_type} onValueChange={(v) => setF({ ...f, customer_type: v })}>
                                <SelectTrigger className="rounded-none border-stone-300 mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <CF label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} />
                        <CF label="Email" v={f.email} on={(v) => setF({ ...f, email: v })} />
                        <CF label="GSTIN" v={f.gst_number} on={(v) => setF({ ...f, gst_number: v })} />
                        <CF label="Address line 1" v={f.address_line1} on={(v) => setF({ ...f, address_line1: v })} />
                        <CF label="Address line 2" v={f.address_line2} on={(v) => setF({ ...f, address_line2: v })} />
                        <CF label="City" v={f.city} on={(v) => setF({ ...f, city: v })} />
                        <CF label="State" v={f.state} on={(v) => setF({ ...f, state: v })} />
                        <CF label="Pincode" v={f.pincode} on={(v) => setF({ ...f, pincode: v })} />
                        <NF label="Default GST %" v={f.default_gst_pct} on={(v) => setF({ ...f, default_gst_pct: v })} />
                        <NF label="Default discount %" v={f.default_discount_pct} on={(v) => setF({ ...f, default_discount_pct: v })} />
                        <NF label="Default special disc (₹)" v={f.default_special_discount_pct} on={(v) => setF({ ...f, default_special_discount_pct: v })} />
                        <NF label="Default cash disc (₹)" v={f.default_cash_discount_pct} on={(v) => setF({ ...f, default_cash_discount_pct: v })} />
                    </div>
                    <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes" className="rounded-none border-stone-300" />
                    <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="cust-save">Save</Button>
                </div>
            )}

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="bg-stone-50 border-b border-stone-200">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Name</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Type</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Phone</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">City</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">State</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">GSTIN</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">GST %</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Disc %</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {items.length === 0 ? <tr><td colSpan={9} className="p-8 text-center text-stone-500">No customers yet.</td></tr> :
                            items.map(c => {
                                const isEdited = dirtyIds.includes(c.id);
                                return (
                                    <tr key={c.id} className={`border-b border-stone-100 ${isEdited ? "bg-amber-50/40" : ""}`} data-testid={`cust-row-${c.id}`}>
                                        <td className="p-2 font-medium">
                                            {bulkMode ? <BulkInput id={c.id} field="name" original={c.name} setCell={setCell} cellValue={cellValue} testId={`cust-name-${c.id}`} /> : c.name}
                                        </td>
                                        <td className="p-2 text-xs uppercase tracking-widest text-stone-500">
                                            {bulkMode ? (
                                                <Select
                                                    value={cellValue(c.id, "customer_type", c.customer_type)}
                                                    onValueChange={(v) => setCell(c.id, "customer_type", v, c.customer_type)}
                                                >
                                                    <SelectTrigger className="rounded-none border-stone-200 h-8 text-xs" data-testid={`cust-type-${c.id}`}><SelectValue /></SelectTrigger>
                                                    <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                                </Select>
                                            ) : c.customer_type}
                                        </td>
                                        <td className="p-2">{bulkMode ? <BulkInput id={c.id} field="phone" original={c.phone} setCell={setCell} cellValue={cellValue} testId={`cust-phone-${c.id}`} /> : c.phone}</td>
                                        <td className="p-2">{bulkMode ? <BulkInput id={c.id} field="city" original={c.city} setCell={setCell} cellValue={cellValue} testId={`cust-city-${c.id}`} /> : c.city}</td>
                                        <td className="p-2">{bulkMode ? <BulkInput id={c.id} field="state" original={c.state} setCell={setCell} cellValue={cellValue} testId={`cust-state-${c.id}`} /> : c.state}</td>
                                        <td className="p-2 text-xs">{bulkMode ? <BulkInput id={c.id} field="gst_number" original={c.gst_number} setCell={setCell} cellValue={cellValue} testId={`cust-gstin-${c.id}`} /> : c.gst_number}</td>
                                        <td className="p-2 text-right text-xs text-stone-500">
                                            {bulkMode ? <BulkNumberInput id={c.id} field="default_gst_pct" original={c.default_gst_pct || 0} setCell={setCell} cellValue={cellValue} testId={`cust-gst-${c.id}`} /> : `${c.default_gst_pct}%`}
                                        </td>
                                        <td className="p-2 text-right text-xs text-stone-500">
                                            {bulkMode ? <BulkNumberInput id={c.id} field="default_discount_pct" original={c.default_discount_pct || 0} setCell={setCell} cellValue={cellValue} testId={`cust-disc-${c.id}`} /> : `${c.default_discount_pct}%`}
                                        </td>
                                        <td className="p-2 text-right space-x-2 whitespace-nowrap">
                                            <button onClick={() => { setEditing(c.id); setF({ ...blank, ...c }); }} className="text-stone-700" title="Edit in dialog" data-testid={`cust-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
                                            <button onClick={() => del(c.id)} className="text-red-700" title="Delete" data-testid={`cust-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const CF = ({ label, v, on, t }) => (<div><Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label><Input value={v || ""} onChange={(e) => on(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid={t} /></div>);
const NF = ({ label, v, on }) => (<div><Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label><Input type="number" step="0.01" value={v} onChange={(e) => on(parseFloat(e.target.value || 0))} className="rounded-none border-stone-300 mt-1" /></div>);

const BulkInput = ({ id, field, original, setCell, cellValue, testId }) => {
    const v = cellValue(id, field, original || "");
    const dirty = v !== (original || "");
    return (
        <Input
            value={v ?? ""}
            onChange={(e) => setCell(id, field, e.target.value, original || "")}
            onFocus={(e) => e.target.select()}
            className={`rounded-none h-8 text-sm ${dirty ? "border-amber-500 bg-amber-50" : "border-stone-200"}`}
            data-testid={testId}
        />
    );
};

const BulkNumberInput = ({ id, field, original, setCell, cellValue, testId }) => {
    const v = cellValue(id, field, original);
    const dirty = parseFloat(v || 0) !== parseFloat(original || 0);
    return (
        <Input
            type="number"
            step="0.01"
            min={0}
            value={v ?? 0}
            onChange={(e) => setCell(id, field, parseFloat(e.target.value || 0), original)}
            onFocus={(e) => e.target.select()}
            className={`rounded-none h-8 text-sm text-right font-mono w-20 ml-auto ${dirty ? "border-amber-500 bg-amber-50" : "border-stone-200"}`}
            data-testid={testId}
        />
    );
};
